import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 350;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 20 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  buttonGroup: {
    display: 'flex',
    gap: '0px',
  },
  button: {
    padding: '4px 12px',
    fontSize: '11px',
    fontWeight: 600,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    border: '1px solid #d1d5db',
    cursor: 'pointer',
    outline: 'none',
    transition: 'background 0.15s, color 0.15s',
  },
  buttonLeft: {
    borderRadius: '6px 0 0 6px',
    borderRight: 'none',
  },
  buttonRight: {
    borderRadius: '0 6px 6px 0',
  },
  buttonActive: {
    background: '#2563eb',
    color: '#fff',
    borderColor: '#2563eb',
  },
  buttonInactive: {
    background: '#fff',
    color: '#6b7280',
    borderColor: '#d1d5db',
  },
  container: {
    overflowX: 'auto',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginTop: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function StreamGraph({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [mode, setMode] = useState('stream');

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.states || data.states.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('width', '100%')
      .attr('height', CHART_HEIGHT)
      .attr('viewBox', `0 0 ${width} ${CHART_HEIGHT}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Build tabular data for d3.stack
    const tableData = data.dates.map((date, i) => {
      const row = { date };
      data.states.forEach((state) => {
        row[state] = (data.series[state] && data.series[state][i]) || 0;
      });
      return row;
    });

    // Stack generator
    const stackGen = d3.stack()
      .keys(data.states)
      .offset(mode === 'stream' ? d3.stackOffsetWiggle : d3.stackOffsetNone)
      .order(d3.stackOrderInsideOut);

    const stacked = stackGen(tableData);

    // Color scale
    const color = d3.scaleOrdinal()
      .domain(data.states)
      .range(d3.schemeTableau10);

    // X scale
    const x = d3.scalePoint()
      .domain(data.dates)
      .range([0, innerW]);

    // Y scale
    const yMin = d3.min(stacked, (layer) => d3.min(layer, (d) => d[0]));
    const yMax = d3.max(stacked, (layer) => d3.max(layer, (d) => d[1]));
    const yPadding = (yMax - yMin) * 0.05 || 1;

    const y = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .range([innerH, 0]);

    // Area generator
    const area = d3.area()
      .x((d) => x(d.data.date))
      .y0((d) => y(d[0]))
      .y1((d) => y(d[1]))
      .curve(d3.curveBasis);

    // Draw layers
    g.selectAll('.stream-layer')
      .data(stacked)
      .enter()
      .append('path')
      .attr('class', 'stream-layer')
      .attr('d', area)
      .attr('fill', (d) => color(d.key))
      .attr('fill-opacity', 0.8)
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.5);

    // X axis
    const tickCount = Math.min(data.dates.length, 12);
    const tickInterval = Math.max(1, Math.floor(data.dates.length / tickCount));
    const tickValues = data.dates.filter((_, i) => i % tickInterval === 0);

    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .tickValues(tickValues)
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Crosshair line (hidden by default)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Invisible overlay columns for tooltip hover
    const colWidth = data.dates.length > 1
      ? innerW / (data.dates.length - 1)
      : innerW;

    data.dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          // Gather all state values for this date index
          const stateValues = data.states
            .map((state) => ({
              state,
              value: (data.series[state] && data.series[state][i]) || 0,
              color: color(state),
            }))
            .sort((a, b) => b.value - a.value);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            stateValues,
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, mode]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0 || !data.states || data.states.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>State Pricing Landscape</div>
            <div style={styles.subtitle}>
              Stacked state contributions to average pest365 pricing over time
            </div>
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No stream data available
        </div>
      </div>
    );
  }

  // Color scale for legend (recreated here to stay in sync with D3 rendering)
  const legendColor = d3.scaleOrdinal()
    .domain(data.states)
    .range(d3.schemeTableau10);

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>State Pricing Landscape</div>
          <div style={styles.subtitle}>
            Stacked state contributions to average pest365 pricing over time
          </div>
        </div>
        <div style={styles.buttonGroup}>
          <button
            style={{
              ...styles.button,
              ...styles.buttonLeft,
              ...(mode === 'stream' ? styles.buttonActive : styles.buttonInactive),
            }}
            onClick={() => setMode('stream')}
          >
            Stream
          </button>
          <button
            style={{
              ...styles.button,
              ...styles.buttonRight,
              ...(mode === 'stacked' ? styles.buttonActive : styles.buttonInactive),
            }}
            onClick={() => setMode('stacked')}
          >
            Stacked
          </button>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        {data.states.map((state) => (
          <div key={state} style={styles.legendItem}>
            <svg width="14" height="14">
              <rect x="0" y="0" width="14" height="14" fill={legendColor(state)} fillOpacity="0.8" rx="2" />
            </svg>
            <span>{state}</span>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.stateValues.map((sv) => (
            <div
              key={sv.state}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="8" height="8">
                  <rect x="0" y="0" width="8" height="8" fill={sv.color} rx="1" />
                </svg>
                {sv.state}
              </span>
              <span style={{ fontWeight: 600, color: '#1f2937' }}>
                ${sv.value != null ? sv.value.toFixed(2) : '--'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
